/**
 * SPEC-Auth-Users-Roles-Audit — client-side role derivation.
 *
 * The role contract (mirrored on the server in `server/middleware/requireAdmin.ts`
 * and the DB CHECK constraint):
 *
 *   role: 'agent' | 'senior' | 'admin' | 'bot' | null
 *
 * Orthogonal flags carried separately on the user row:
 *
 *   is_admin — admin-capable users whose primary role is NOT 'admin'
 *              (Sarah's case: role='senior', is_admin=true)
 *   dev      — debug-surface access (Sunny's case)
 *
 * `deriveRoleValue` is the single source of truth for the RoleContext value
 * the AuthenticatedApp passes to its children. The payload comes from
 * `/api/auth/check-approved`:
 *
 *   { approved, role, isAdmin, dev, name, userId, email }
 *
 * `isAdmin` on RoleValue is the EFFECTIVE admin predicate: true when the
 * server says `role==='admin'` OR `isAdmin===true`.
 */

export const ROLE_AGENT = "agent" as const;
export const ROLE_SENIOR = "senior" as const;
export const ROLE_ADMIN = "admin" as const;
export const ROLE_BOT = "bot" as const;
// SPEC-TrainingSimulator §3 (D16) — trainee routing lands in Phase B.
export const ROLE_TRAINEE = "trainee" as const;

export type Role = typeof ROLE_AGENT | typeof ROLE_SENIOR | typeof ROLE_ADMIN | typeof ROLE_BOT | typeof ROLE_TRAINEE;

export interface ApprovalData {
  approved?: boolean;
  email?: string | null;
  role?: Role | null;
  isAdmin?: boolean | null;
  dev?: boolean | null;
  name?: string | null;
  userId?: string | null;
}

export interface RoleValue {
  role: Role | null;
  isAgent: boolean;
  isSenior: boolean;
  isAdmin: boolean; // EFFECTIVE — role==='admin' OR isAdmin flag === true
  isBot: boolean;
  dev: boolean;
  name: string | null;
  email: string | null;
  userId: string | null;
}

export function isAdminEffective(
  data: { role?: Role | string | null; isAdmin?: boolean | null } | null | undefined,
): boolean {
  if (!data) return false;
  return data.role === "admin" || data.isAdmin === true;
}

export function deriveRoleValue(
  approvalData: ApprovalData | null | undefined,
): RoleValue {
  const role = (approvalData?.role ?? null) as Role | null;
  return {
    role,
    isAgent: role === "agent",
    isSenior: role === "senior",
    isAdmin: isAdminEffective(approvalData),
    isBot: role === "bot",
    dev: approvalData?.dev === true,
    name: approvalData?.name ?? null,
    email: approvalData?.email ?? null,
    userId: approvalData?.userId ?? null,
  };
}

/**
 * Predicate used by AdminRoute. A non-effective-admin is redirected to `/`.
 */
export function shouldRedirectFromAdminRoute(
  data: { role?: Role | string | null; isAdmin?: boolean | null } | null | undefined,
): boolean {
  return !isAdminEffective(data);
}
